import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import VinScanner from '../components/VinScanner.js';
import PhotoCapture from '../components/PhotoCapture.js';
import { api } from '../services/api-client.js';

type Step = 'vin' | 'photos' | 'mismatch' | 'done';

type VinState = 'idle' | 'creating' | 'error';

interface VehicleData {
  vin: string;
  vehicleId: string | null;
  decodeInfo: { year?: number; make?: string; model?: string; trim?: string } | null;
  photoCount: number;
  mileage: number | null;
}

export default function AddVehiclePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('vin');
  const [data, setData] = useState<VehicleData>({
    vin: '',
    vehicleId: null,
    decodeInfo: null,
    photoCount: 0,
    mileage: null,
  });
  const [error, setError] = useState('');
  const [vinState, setVinState] = useState<VinState>('idle');
  const [mismatchWarnings, setMismatchWarnings] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [keeping, setKeeping] = useState(false);
  const [activating, setActivating] = useState(false);
  const lastVinRef = useRef('');
  const prefillUsed = useRef(false);

  // Auto-submit VIN from auction scan (passed via navigation state)
  useEffect(() => {
    const state = location.state as { vin?: string } | null;
    if (state?.vin && !prefillUsed.current && step === 'vin') {
      prefillUsed.current = true;
      handleVinScanned(state.vin);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step 1: VIN scanned — create vehicle, then advance to photos
  const handleVinScanned = useCallback((vin: string) => {
    setData((d) => ({ ...d, vin }));
    setError('');
    setVinState('creating');
    lastVinRef.current = vin;

    api.post<any>('/vehicles', { vin, mileage: 0 }).then((res) => {
      if (res.success && res.data) {
        setData((d) => ({
          ...d,
          vehicleId: res.data.id,
          decodeInfo: {
            year: res.data.year,
            make: res.data.make,
            model: res.data.model,
            trim: res.data.trim,
          },
        }));
        setVinState('idle');
        setStep('photos');
      } else {
        setError(res.error ?? 'Failed to decode VIN');
        setVinState('error');
      }
    }).catch(() => {
      setError('Network error — check your connection');
      setVinState('error');
    });
  }, []);

  // Step 2: Photos + mileage + notes done — activate vehicle + run AI pipeline
  const handleDone = useCallback(async (count: number, mileage: number, notes: string) => {
    setData((d) => ({ ...d, photoCount: count, mileage }));
    setActivating(true);

    if (data.vehicleId) {
      try {
        const res = await api.patch<any>(`/vehicles/${data.vehicleId}`, {
          mileage,
          status: 'active',
          ...(notes ? { description: notes } : {}),
        });

        queryClient.invalidateQueries({ queryKey: ['vehicles'] });

        // Check for VIN/photo mismatch
        if (res.pipeline?.mismatch_detected) {
          setMismatchWarnings(res.pipeline.warnings ?? []);
          setActivating(false);
          setStep('mismatch');
          return;
        }
      } catch {
        // Pipeline failed but vehicle was created — still go to done
      }
    }

    setActivating(false);
    setStep('done');
  }, [data.vehicleId, queryClient]);

  const title = data.decodeInfo
    ? [data.decodeInfo.year, data.decodeInfo.make, data.decodeInfo.model].filter(Boolean).join(' ')
    : data.vin;

  // Step indicators
  const steps = ['VIN', 'Photos', 'Done'];
  const stepIndex = { vin: 0, photos: 1, mismatch: 2, done: 2 }[step];

  return (
    <div className="page">
      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-2 py-6">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full transition-colors ${
                i <= stepIndex ? 'bg-brand-500' : 'bg-surface-700'
              }`}
            />
            {i < steps.length - 1 && (
              <div className={`w-8 h-0.5 ${i < stepIndex ? 'bg-brand-500' : 'bg-surface-700'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      {step === 'vin' && (
        <div className="py-8">
          {vinState === 'creating' ? (
            <div className="text-center py-16">
              <div className="w-10 h-10 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-surface-300 font-semibold">Decoding VIN...</p>
              <p className="text-surface-500 text-sm mt-1 font-mono tracking-wider">{lastVinRef.current}</p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-center mb-2">Scan VIN</h2>
              <p className="text-surface-500 text-center text-sm mb-8">
                Point camera at VIN barcode or type it in
              </p>
              <VinScanner onVinScanned={handleVinScanned} />
              {error && (
                <div className="mt-4 text-center">
                  <p className="text-red-400 mb-3">{error}</p>
                  <button
                    onClick={() => handleVinScanned(lastVinRef.current)}
                    className="btn-primary px-6"
                  >
                    Retry
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {step === 'photos' && (
        <div className="py-8">
          {activating ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
              <h2 className="text-xl font-bold mb-2">Setting up your listing...</h2>
              <p className="text-surface-500 text-sm">AI is analyzing photos, pulling market data, and writing your listing. This takes about 30 seconds.</p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-center mb-2">{title || 'Add Details'}</h2>
              <p className="text-surface-500 text-center text-sm mb-8">
                Photos, mileage, and you're done
              </p>
              {data.vehicleId ? (
                <PhotoCapture
                  vehicleId={data.vehicleId}
                  vehicleTitle={title}
                  onDone={handleDone}
                />
              ) : (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-surface-500">Setting up vehicle...</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {step === 'mismatch' && (
        <div className="py-12 text-center">
          {/* Warning icon */}
          <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>

          <h2 className="text-2xl font-bold mb-2 text-yellow-400">VIN / Photo Mismatch</h2>
          <p className="text-surface-400 mb-4">The photos don't appear to match the VIN.</p>

          {mismatchWarnings.map((w, i) => (
            <div key={i} className="card p-4 mb-3 text-left border-yellow-800 bg-yellow-900/10">
              <p className="text-sm text-yellow-300">{w}</p>
            </div>
          ))}

          <p className="text-surface-500 text-sm mb-6">
            This vehicle has been flagged and won't appear in your active inventory. You can delete it and start over, or keep it anyway if you're sure.
          </p>

          <div className="space-y-3">
            <button
              onClick={async () => {
                if (!data.vehicleId) return;
                setDeleting(true);
                try {
                  await api.delete(`/vehicles/${data.vehicleId}`);
                  queryClient.invalidateQueries({ queryKey: ['vehicles'] });
                  // Reset to start
                  setStep('vin');
                  setData({ vin: '', vehicleId: null, decodeInfo: null, photoCount: 0, mileage: null });
                  setError('');
                  setVinState('idle');
                  setMismatchWarnings([]);
                } catch {
                  setError('Failed to delete vehicle');
                }
                setDeleting(false);
              }}
              disabled={deleting}
              className="btn-primary w-full bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete & Start Over'}
            </button>
            <button
              onClick={async () => {
                if (!data.vehicleId) return;
                setKeeping(true);
                try {
                  await api.patch(`/vehicles/${data.vehicleId}`, { status: 'active' });
                  queryClient.invalidateQueries({ queryKey: ['vehicles'] });
                  setStep('done');
                } catch {
                  setError('Failed to update vehicle');
                }
                setKeeping(false);
              }}
              disabled={keeping}
              className="btn-ghost w-full text-sm"
            >
              {keeping ? 'Saving...' : 'Keep Anyway — Photos Are Correct'}
            </button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="py-12 text-center">
          {/* Success icon */}
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h2 className="text-2xl font-bold mb-2">Listed!</h2>
          <p className="text-surface-400 mb-1">{title}</p>
          <p className="text-surface-600 text-sm">
            {data.photoCount} photo{data.photoCount !== 1 ? 's' : ''} &middot; {data.mileage?.toLocaleString() ?? '—'} miles
          </p>

          {/* Platform connect prompt */}
          <div className="card p-5 mt-6 text-left border border-surface-700">
            <p className="font-semibold text-sm mb-1">Post everywhere?</p>
            <p className="text-surface-500 text-xs mb-3">Connect your accounts to auto-post listings</p>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/marketplace?tab=platforms')}
                className="flex-1 py-2 px-3 rounded-lg bg-[#1877F2]/20 text-[#1877F2] text-xs font-semibold hover:bg-[#1877F2]/30 transition-colors"
              >
                Facebook
              </button>
              <button
                onClick={() => navigate('/marketplace?tab=platforms')}
                className="flex-1 py-2 px-3 rounded-lg bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-pink-400 text-xs font-semibold hover:from-purple-500/30 hover:to-pink-500/30 transition-colors"
              >
                Instagram
              </button>
              <button
                onClick={() => navigate('/marketplace?tab=platforms')}
                className="flex-1 py-2 px-3 rounded-lg bg-orange-500/20 text-orange-400 text-xs font-semibold hover:bg-orange-500/30 transition-colors"
              >
                Craigslist
              </button>
            </div>
          </div>

          <div className="space-y-3 mt-6">
            <button
              onClick={() => navigate(`/vehicles/${data.vehicleId}`)}
              className="btn-primary w-full"
            >
              View Vehicle
            </button>
            <button
              onClick={() => {
                setStep('vin');
                setData({ vin: '', vehicleId: null, decodeInfo: null, photoCount: 0, mileage: null });
                setError('');
                setVinState('idle');
              }}
              className="btn-secondary w-full"
            >
              Add Another
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-ghost w-full text-sm"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
